import useAutomaticDiscountPromotion from './useAutomaticDiscountPromotion'
import { useFreeGiftPromotion } from './useFreeGiftPromotion'
import { usePromotionRemover } from './usePromotionRemover'

let inProgress = false

interface UsePromotionControllerParams {
  setup?: boolean
}

interface ApplyResult {
  success?: boolean
  reason?: string
  extra?: any
}

let promotionInProgress = Promise.resolve()
const promotionsApplied = ref(false)

export const usePromotionController = (
  params: UsePromotionControllerParams = {},
) => {
  const { user } = useAgsUser()
  const basket = useAgsBasket()
  const log = useAgsLog('usePromotionController')

  if (params.setup) {
    usePromotionRemover()
  }

  const promotionTypes = [
    useFreeGiftPromotion(false),
    useAutomaticDiscountPromotion(),
    // add more types here ...
  ] as Array<{ tryApply: (promotion: Promotion) => Promise<ApplyResult> }>

  // Apply promotions in order of priority
  async function tryApplyPromotions(promotions: Promotion[]) {
    const successfulPromotions = [] as Promotion[]
    // Sort promotions by priority
    promotions.sort((a, b) => a.priority - b.priority)

    // Apply promotions in order of priority
    const promotion = promotions[0] ?? null
    if (promotion) {
      const { from, to } = promotion.schedule
      const notActive = !promotion.isActive
      const hasntStarted = from && new Date(from) > new Date()
      const hasEnded = to && new Date(to) < new Date()

      if (notActive || hasntStarted || hasEnded) {
        log.info('skip-promotion', {
          reason: {
            notActive,
            hasntStarted,
            hasEnded,
          },
          name: promotion.name,
          promotion,
        })
        return
      }

      log.info('try-apply', {
        name: promotion.name,
        promotion,
      })

      let result: ApplyResult
      // Try to apply promotion with each promotion type
      for (const promotionType of promotionTypes) {
        result = await promotionType.tryApply(promotion)
        if (result.success) {
          successfulPromotions.push(promotion)
          break
        }
      }
      log.info('promotion-result', {
        type: result!.success
          ? '✅ promotion-applied'
          : '👎 promotion-not-applied',
        success: result!.success,
        reason: result!.reason,
        extra: result!.extra,
        promotion,
      })
    }
  }

  async function update(trigger: string) {
    if (inProgress) return
    inProgress = true
    promotionsApplied.value = false
    try {
      let promotions: Promotion[] = []
      const applicablePromotions = basket.data.value?.applicablePromotions
      if (applicablePromotions) {
        promotions = applicablePromotions.map((k) => k.promotion)
      } else {
        log.info('skip', { type: 'skip', reason: 'no promotions', trigger })
        return
      }
      log.info('try-apply-promotions', {
        trigger,
        promotions,
      })
      await (promotionInProgress = tryApplyPromotions(promotions))
      log.info('pending', { type: '---' })
    } finally {
      inProgress = false
      promotionsApplied.value = true
    }
  }

  if (params.setup) {
    watch(basket.data, () => update('basket'))
    watch(user, () => update('user'))
  }

  return {
    promotionInProgress,
    promotionsApplied,
  }
}
